/**
 * AuthContext — SysOps Panel.
 *
 * On mount:
 *   1. ensureMsalReady (initialize + finalize redirect round-trip)
 *   2. acquireToken silent → loginPopup fallback (driven by getMsalToken)
 *   3. resolve `{ upn, displayName }` from the active MSAL account
 *   4. fetch role / permissions via `apiClient.retail.config.users.byEmail(upn)`
 *      — same shape Wave 6.1 already populates for ROP-teams
 *
 * Roles are surfaced flat (string[]). The role-gate component checks for
 * `SystemOps` or `SuperAdmin`. No bypass key, no test-mode escape — the
 * gate is the only entry point.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { ApiError } from '@retail-ops/api-client';
import { ensureMsalReady, getMsalToken, msalInstance } from '@/auth/msal';
import {
  createApiClient,
  apiClientFactoryOptionsFromEnv,
} from '@/services/apiClient';
import { canonicalRoleCode } from '@retail-ops/panel-shell';

export type AuthStatus = 'loading' | 'ok' | 'unprovisioned' | 'initializing' | 'error';

export interface AuthUser {
  upn: string;
  displayName: string;
  tenantId?: string;
  roles: ReadonlyArray<string>;
  permissions: Record<string, boolean>;
  status: AuthStatus;
}

export interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  loading: boolean;
  error: string | null;
  status: AuthStatus;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function normalizeRoleCodes(roles: ReadonlyArray<unknown> | undefined): string[] {
  if (!roles || roles.length === 0) return [];
  const out: string[] = [];
  for (const r of roles) {
    if (typeof r === 'string') {
      const t = r.trim();
      if (t) out.push(canonicalRoleCode(t));
      continue;
    }
    if (r && typeof r === 'object') {
      const obj = r as Record<string, unknown>;
      const code =
        (typeof obj['roleCode'] === 'string' && obj['roleCode']) ||
        (typeof obj['code'] === 'string' && obj['code']) ||
        (typeof obj['role'] === 'string' && obj['role']) ||
        '';
      if (code) out.push(canonicalRoleCode(code));
    }
  }
  return out;
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');
  const inflight = useRef<boolean>(false);

  const bootstrap = useCallback(async (): Promise<void> => {
    if (inflight.current) return;
    inflight.current = true;
    setLoading(true);
    setError(null);
    setStatus('loading');
    try {
      await ensureMsalReady();
      const t = await getMsalToken();
      setToken(t);

      const accounts = msalInstance.getAllAccounts();
      const acct = accounts[0];
      const upn = acct?.username ?? 'unknown@mypcb.com';
      const displayName = acct?.name ?? upn;
      const tenantId = acct?.tenantId;

      const client = createApiClient(apiClientFactoryOptionsFromEnv());
      try {
        const envelope = await client.retail.config.users.byEmail(upn);
        if (envelope.success && envelope.data) {
          const raw = envelope.data as unknown as Record<string, unknown>;
          const rolesRaw = Array.isArray(raw['roles'])
            ? (raw['roles'] as unknown[])
            : [];
          const roles = normalizeRoleCodes(rolesRaw);

          const permissionsRaw = raw['permissions'];
          const permissions: Record<string, boolean> = {};
          if (Array.isArray(permissionsRaw)) {
            for (const p of permissionsRaw) {
              if (p && typeof p === 'object') {
                const po = p as Record<string, unknown>;
                const code =
                  (typeof po['permissionCode'] === 'string' && po['permissionCode']) ||
                  (typeof po['code'] === 'string' && po['code']) ||
                  '';
                const granted =
                  typeof po['isGranted'] === 'boolean' ? po['isGranted'] : true;
                if (code) permissions[code] = granted;
              }
            }
          } else if (permissionsRaw && typeof permissionsRaw === 'object') {
            for (const [k, v] of Object.entries(permissionsRaw)) {
              permissions[k] = Boolean(v);
            }
          }

          const next: AuthUser = {
            upn,
            displayName,
            ...(tenantId !== undefined ? { tenantId } : {}),
            roles,
            permissions,
            status: 'ok',
          };
          setUser(next);
          setStatus('ok');
          return;
        }
        setStatus('unprovisioned');
        setUser({
          upn,
          displayName,
          ...(tenantId !== undefined ? { tenantId } : {}),
          roles: [],
          permissions: {},
          status: 'unprovisioned',
        });
        setError(envelope.error ?? 'User not provisioned in ROP user roles.');
      } catch (e) {
        if (ApiError.isApiError(e)) {
          if (e.httpStatus === 503) {
            setStatus('initializing');
            setUser({
              upn,
              displayName,
              ...(tenantId !== undefined ? { tenantId } : {}),
              roles: [],
              permissions: {},
              status: 'initializing',
            });
            setError('User role config initializing. Please retry shortly.');
            return;
          }
          if (e.httpStatus === 404) {
            setStatus('unprovisioned');
            setUser({
              upn,
              displayName,
              ...(tenantId !== undefined ? { tenantId } : {}),
              roles: [],
              permissions: {},
              status: 'unprovisioned',
            });
            setError(
              'Your account is not provisioned for the SysOps Panel. Contact a SuperAdmin to assign the SystemOps role.',
            );
            return;
          }
        }
        const msg = e instanceof Error ? e.message : String(e);
        setStatus('error');
        setError(`Could not resolve user role: ${msg}`);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown auth error';
      setStatus('error');
      setError(msg);
    } finally {
      setLoading(false);
      inflight.current = false;
    }
  }, []);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  const logout = useCallback(async (): Promise<void> => {
    const accounts = msalInstance.getAllAccounts();
    if (accounts[0]) {
      await msalInstance.logoutRedirect({ account: accounts[0] });
    }
    setUser(null);
    setToken(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      token,
      loading,
      error,
      status,
      logout,
      refresh: bootstrap,
    }),
    [user, token, loading, error, status, logout, bootstrap],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
